/**
 * Times page composable
 *
 * Main orchestrator that combines smaller composables:
 * - useTimelinesData: Data fetching and progress
 * - useTimelineUnlock: Prerequisite-based unlock logic
 * - useTimelineOptions: Option selection modal
 */
import { reactive, onMounted, onBeforeUnmount } from '@vue/composition-api';
import Vue from 'vue';
import { getters, mutations } from "@/components/game/store";
import { bannerProps } from "@/components/game/shared/baseBanner";
import eventBus from "@/components/game/store/eventBus";

// Import smaller composables
import { useTimelinesData } from './composables/useTimelinesData';
import { useTimelineUnlock } from './composables/useTimelineUnlock';
import { useTimelineOptions } from './composables/useTimelineOptions';

export function useTimesPage(props) {
  // Use smaller composables
  const {
    timelines,
    progress,
    isFirstTimelineFound,
    visibleTimelines,
    loadTimelines,
    markAsFound,
    getTimelineContent
  } = useTimelinesData();

  const {
    initHadFound,
    isUnlocked,
    updateReceivedHadFoundItem: updateHadFound
  } = useTimelineUnlock();

  const {
    isCardModalActive,
    currentTimeline,
    openOptions,
    hideOptions,
    isCorrectOption
  } = useTimelineOptions();

  // UI state
  const icon = reactive({
    lockTime: '',
    findClue: '',
    line: '',
    node: '',
    node_locked: ''
  });

  const background = reactive({
    mainBg: ''
  });

  const bannerPropsRef = reactive({ ...bannerProps });

  // Helper: pick random content from array
  const sampleContent = (contents) => {
    if (!contents || !contents.length) return '';
    return contents[Math.floor(Math.random() * contents.length)];
  };

  // Initialize view data from topic info
  const initViewData = () => {
    const topicInfo = getters.getTopicInfo('timeline');
    bannerPropsRef.imgSrc = topicInfo.banner;
    bannerPropsRef.content = sampleContent(topicInfo.banner_content);
    bannerPropsRef.styles = "top: 50px; left: 155px;";
    bannerPropsRef.banner_avatar = topicInfo.banner_avatar;
    Vue.set(icon, 'line', topicInfo.line);
    Vue.set(icon, 'node', topicInfo.node);
    Vue.set(icon, 'node_locked', topicInfo.node_locked);
    Vue.set(icon, 'lockTime', topicInfo.locked_text);
    Vue.set(icon, 'findClue', topicInfo.search);
    background.mainBg = topicInfo.background;
  };

  // Get timeline content (result if found, report if not)
  const timelineContent = (timeline) => {
    return getTimelineContent(timeline, isUnlocked(timeline));
  };

  // Get node icon based on first found status
  const getNodeIcon = () => {
    return isFirstTimelineFound.value ? icon.node : icon.node_locked;
  };

  // Open find clue modal
  const findClue = (index) => {
    const timeline = timelines.value[index];
    openOptions(timeline);
  };

  // Handle option selection
  const selectedOption = (option) => {
    hideOptions();

    // Always send to backend - broadcasts via WebSocket
    if (props.updateGroupStoryShip) {
      props.updateGroupStoryShip('timeline', currentTimeline.eventTime, { timeline_option: option });
    }

    // Update local state if correct
    if (isCorrectOption(option)) {
      markAsFound(currentTimeline);
    }
  };

  // Handle WebSocket updates - also update progress for timeline type
  const updateReceivedHadFoundItem = (itemType, item) => {
    if (!item) return;

    updateHadFound(itemType, item);

    // If it's a timeline, also mark as found in progress
    if (itemType === 'hadFoundTimelineIds') {
      markAsFound(item);
    }
  };

  const onLoaded = () => {
    mutations.closeLoading();
  };

  // Lifecycle
  onMounted(async () => {
    try {
      initViewData();
      const data = await loadTimelines(props.groupId, props.memberLineId);
      initHadFound(data.hadFound);

      if (data.demo && props.updateFinishedDemo) {
        props.updateFinishedDemo();
      }
    } catch (error) {
      console.error(`遊戲服務中斷: ${error.message}`);
    }

    eventBus.$on('received', updateReceivedHadFoundItem);
  });

  onBeforeUnmount(() => {
    eventBus.$off('received', updateReceivedHadFoundItem);
  });

  // Return only what template needs
  return {
    // Data
    timelines,
    currentTimeline,
    progress,
    icon,
    background,
    bannerProps: bannerPropsRef,

    // Computed
    isFirstTimelineFound,
    visibleTimelines,

    // Modal state
    isCardModalActive,

    // Methods
    timelineContent,
    getNodeIcon,
    isUnlocked,
    findClue,
    selectedOption,
    hideOptions,
    onLoaded
  };
}
